import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { Logo, LogoMark } from "@/components/brand/logo";
import { APP_ROUTES, NavGroup } from "@/config/routes.config";
import useAuthUser from "@/hooks/useAuthUser";
import { cn } from "@/lib/utils";
import { ChevronLeft, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { PATH_HOME } from "@/routes/paths";
import { useInboxStore } from "@/store/inbox/inbox.store";

// TODO: replace with backend-provided workspace data once the API exposes it.
const COMPANY_NAME = "NexgenT";
const WORKSPACE_SUBTITLE = "Workspace · Pro plan";

const NAV_GROUPS: { key: NavGroup; label: string }[] = [
  { key: "workspace", label: "Workspace" },
  { key: "crm", label: "CRM" },
  { key: "acquire", label: "Acquire" },
  { key: "automate", label: "Automate" },
];

// Company logo with initial fallback: uses the /me logo when present (or when
// the image fails to load, e.g. a broken signed URL).
const CompanyAvatar = ({ logo }: { logo?: string | null }) => {
  const [imgFailed, setImgFailed] = useState(false);
  const showImage = !!logo && !imgFailed;
  return (
    <div className="grid h-8 w-8 shrink-0 place-items-center overflow-hidden rounded-lg bg-white/[0.04] ring-1 ring-white/10">
      {showImage ? (
        <img
          src={logo}
          alt={COMPANY_NAME}
          className="h-full w-full object-contain p-1"
          onError={() => setImgFailed(true)}
        />
      ) : (
        <span className="text-[13px] font-semibold text-white">{COMPANY_NAME.charAt(0)}</span>
      )}
    </div>
  );
};

/** Unread Inbox messages for the sidebar badge, refreshed every 30 s for users who can see the Inbox. */
const INBOX_BADGE_POLL_MS = 30_000;
const useInboxUnreadCount = () => {
  const { user, hasPermission } = useAuthUser();
  const canSeeInbox = Boolean(user.isAdmin) || hasPermission("inbox:access");
  const unread = useInboxStore((s) => s.unreadCount);
  const fetchUnreadCount = useInboxStore((s) => s.fetchUnreadCount);

  useEffect(() => {
    if (!canSeeInbox) return;
    void fetchUnreadCount();
    const t = setInterval(() => void fetchUnreadCount(), INBOX_BADGE_POLL_MS);
    return () => clearInterval(t);
  }, [canSeeInbox, fetchUnreadCount]);

  return canSeeInbox ? unread : 0;
};

const AppSidebar = () => {
  const { pathname } = useLocation();
  const { user, hasPermission } = useAuthUser();
  const { open, state, isMobile, setOpenMobile, toggleSidebar } = useSidebar();
  const isCollapsed = state === "collapsed";
  const inboxUnread = useInboxUnreadCount();

  // `public` routes are always visible. Legacy admins (user.isAdmin) see every route.
  // Otherwise the route is shown only when the user holds at least one of its
  // `requiredPermissions`.
  const isRouteVisible = (route: (typeof APP_ROUTES)[number]) => {
    if (route.public || user.isAdmin) return true;
    return route.requiredPermissions?.length ? hasPermission(route.requiredPermissions) : false;
  };

  const visibleGroups = NAV_GROUPS.map((g) => ({
    ...g,
    routes: APP_ROUTES.filter((r) => r.group === g.key && isRouteVisible(r)),
  })).filter((g) => g.routes.length > 0);

  const footerRoutes = APP_ROUTES.filter((route) => route.section === "footer" && isRouteVisible(route));

  useEffect(() => {
    if (isMobile) setOpenMobile(false);
  }, [pathname, isMobile, setOpenMobile]);

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="p-4">
        <div className="flex justify-between items-center w-full">
          <Link
            to={PATH_HOME}
            className={cn("flex gap-3 items-center cursor-pointer", isCollapsed && !isMobile && "md:m-auto")}
          >
            {/* Demo-ui brand: SVG mark with blue glow + 18px wordmark */}
            {state === "expanded" || isMobile ? <Logo /> : <LogoMark className="h-7 w-auto" />}
          </Link>
          {isMobile && (
            <button
              type="button"
              aria-label="Close menu"
              onClick={() => setOpenMobile(false)}
              className="grid h-8 w-8 place-items-center rounded-lg border border-white/[0.06] bg-white/[0.02] text-slate-300 transition hover:border-white/[0.14] hover:text-white"
            >
              <X className="h-4 w-4" />
            </button>
          )}

          {/* Desktop collapse/expand chevron (demo-ui): inline in the header
              when expanded; floats half out of the rail when collapsed. */}
          {!isMobile && (
            <button
              type="button"
              aria-label="Toggle sidebar"
              onClick={toggleSidebar}
              className={cn(
                "hidden md:grid h-8 w-8 shrink-0 place-items-center rounded-xl border border-white/[0.06] bg-white/[0.02] text-slate-300 transition hover:border-white/[0.14] hover:bg-white/[0.05] hover:text-white",
                isCollapsed && "md:absolute md:-right-3 md:top-5 md:z-50 md:border-white/10 md:!bg-ink-800 md:shadow-card",
              )}
            >
              <ChevronLeft className={cn("h-4 w-4 transition-transform", isCollapsed && "rotate-180")} />
            </button>
          )}
        </div>
      </SidebarHeader>

      {/* Tenant chip (demo-ui): workspace identity below the brand */}
      <div className="mx-3 mt-1">
        <div
          className={cn(
            "flex items-center gap-3 rounded-xl border border-white/[0.06] bg-white/[0.02] p-2 transition hover:border-white/[0.12]",
            isCollapsed && !isMobile && "justify-center",
          )}
        >
          <CompanyAvatar logo={user?.logo} />
          {(open || isMobile) && (
            <div className="flex min-w-0 flex-1 flex-col leading-tight">
              <span className="truncate text-[13px] font-semibold text-white">{COMPANY_NAME}</span>
              <span className="truncate text-[11px] text-slate-400">{WORKSPACE_SUBTITLE}</span>
            </div>
          )}
        </div>
      </div>

      <SidebarContent className="scroll-custom">
        {visibleGroups.map((group, idx) => (
          <SidebarGroup key={group.key}>
            {isCollapsed && !isMobile ? (
              /* Thin divider between groups in icon/collapsed mode */
              idx > 0 && (
                <div aria-hidden className="mx-3 my-1 h-px bg-white/[0.06]" />
              )
            ) : (
              <SidebarGroupLabel className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                {group.label}
              </SidebarGroupLabel>
            )}
            <SidebarGroupContent>
              <SidebarMenu className="gap-1">
                {group.routes.map((item) => {
                  const isActive = item.path === pathname;
                  const badgeCount = item.badge === "inbox-unread" ? inboxUnread : 0;
                  return (
                    <SidebarMenuItem key={item.path}>
                      <SidebarMenuButton
                        className="mx-auto"
                        tooltip={item.title}
                        size="lg"
                        asChild
                        isActive={isActive}
                      >
                        <Link
                          to={item.path}
                          className={cn(
                            "group/nav relative rounded-xl text-[13px] font-medium text-slate-400 transition hover:bg-white/[0.03] hover:text-white",
                            isActive && "!text-white bg-gradient-to-r from-brand-500/15 to-brand-500/5"
                          )}
                        >
                          {/* Active indicator bar (demo-ui sidebar) */}
                          {isActive && (
                            <span
                              aria-hidden
                              className="absolute inset-y-1.5 left-0 w-[3px] rounded-r-full bg-gradient-to-b from-brand-300 to-brand-500 shadow-[0_0_18px_rgba(59,130,246,0.65)]"
                            />
                          )}
                          {item.icon && (
                            <item.icon
                              className={cn(
                                "!h-[18px] !w-[18px] shrink-0 transition",
                                isActive ? "text-brand-200" : "text-slate-400 group-hover/nav:text-white",
                                { "mx-auto": !open && !isMobile },
                              )}
                            />
                          )}
                          {(open || isMobile) && <span className="flex-1 truncate">{item.title}</span>}
                          {(open || isMobile) && badgeCount > 0 && (
                            <span className="ml-auto flex h-5 min-w-[20px] items-center justify-center rounded-full bg-brand-500 px-1 text-[10px] font-semibold text-white">
                              {badgeCount > 99 ? "99+" : badgeCount}
                            </span>
                          )}
                          {(open || isMobile) && item.badge === "beta" && (
                            <span className="ml-auto rounded-md bg-brand-500/20 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-brand-200 ring-1 ring-brand-400/30">
                              Beta
                            </span>
                          )}
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>

      <SidebarFooter>
        <SidebarGroupLabel
          className={cn(
            "text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500",
            { "mx-auto": isCollapsed && !isMobile },
          )}
        >
          Others
        </SidebarGroupLabel>
        <SidebarMenu className="gap-1">
          {footerRoutes.map((item) => (
            <SidebarMenuItem key={item.path}>
              <SidebarMenuButton className="mx-auto" tooltip={item.title} size="lg" asChild>
                <Link
                  to={item.path}
                  className="group/nav rounded-xl text-[13px] font-medium text-slate-400 transition hover:bg-white/[0.03] hover:text-white"
                >
                  {item.icon && (
                    <item.icon
                      className={cn(
                        "!h-[18px] !w-[18px] shrink-0 text-slate-400 transition group-hover/nav:text-white",
                        { "mx-auto": !open && !isMobile },
                      )}
                    />
                  )}
                  {(open || isMobile) && <span className="flex-1 truncate">{item.title}</span>}
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          ))}
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
};

export default AppSidebar;
